import { SxProps, Theme } from "@mui/material";
import brandingTokens, {
  brandingResponsiveTokens as r,
} from "./Branding.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

const {
  layout,
  card,
  header,
  masterSwitch,
  toggle,
  logo,
  field,
  textarea,
  brandColor,
  preview,
  email,
  primaryButton,
} = brandingTokens;

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
  md: layout.gap,
};

const brandingStyles = {
  /** Settings stack + preview panel; wraps to one column on narrow viewports. */
  columns: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: stackGap,
    // The reference closes the screen with "Save Changes", so the header row
    // that carries it is pushed past this block below `md`.
    order: { xs: 1, md: 0 },
    [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
  } as SxProps<Theme>,

  settingsColumn: {
    flex: `1 1 ${layout.settingsBasis}`,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: stackGap,
    [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
  } as SxProps<Theme>,

  /** Everything below the master switch dims while branding is off. */
  gatedCards: (enabled: boolean) =>
    ({
      display: "flex",
      flexDirection: "column",
      gap: stackGap,
      [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
      opacity: enabled ? 1 : masterSwitch.disabledOpacity,
      transition: masterSwitch.opacityTransition,
    }) as SxProps<Theme>,

  card: {
    bgcolor: "background.paper",
    border: `1px solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    boxSizing: "border-box",
    p: { xs: r.card.padding, md: card.padding },
    display: "flex",
    flexDirection: "column",
    gap: { xs: r.card.gap, md: card.gap },
  } as SxProps<Theme>,

  /** Single-row card holding the master switch — shorter than the others. */
  switchCard: {
    bgcolor: "background.paper",
    border: `1px solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    boxSizing: "border-box",
    p: { xs: r.card.padding, md: card.paddingCompact },
    display: "flex",
    alignItems: { xs: "flex-start", md: "center" },
    gap: masterSwitch.gap,
  } as SxProps<Theme>,

  switchCardText: {
    display: "flex",
    flexDirection: "column",
    gap: masterSwitch.textGap,
    minWidth: 0,
  } as SxProps<Theme>,

  switchCardTitle: {
    color: header.titleColor,
    fontSize: masterSwitch.titleFontSize,
    fontWeight: masterSwitch.titleFontWeight,
    lineHeight: "normal",
  } as SxProps<Theme>,

  cardHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
  } as SxProps<Theme>,

  /** Loading variant of `cardHeaderText` — grows so the lines fill the row. */
  skeletonHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  cardTitle: {
    color: header.titleColor,
    fontSize: { xs: r.header.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  cardDescription: {
    color: header.descriptionColor,
    fontSize: {
      xs: r.header.descriptionFontSize,
      md: header.descriptionFontSize,
    },
    lineHeight: header.descriptionLineHeight,
  } as SxProps<Theme>,

  toggleWrapper: {
    ml: "auto",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: toggle.lockGap,
  } as SxProps<Theme>,

  toggleTrack: (checked: boolean) =>
    ({
      position: "relative",
      flexShrink: 0,
      width: toggle.width,
      height: toggle.height,
      p: 0,
      borderRadius: "999px",
      bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      transition: toggle.transition,
      "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
      "&.Mui-disabled": {
        bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  toggleKnob: (checked: boolean) =>
    ({
      position: "absolute",
      top: toggle.knobTop,
      left: 0,
      width: toggle.knobSize,
      height: toggle.knobSize,
      borderRadius: "50%",
      bgcolor: toggle.knobBg,
      boxShadow: toggle.knobShadow,
      transform: checked ? toggle.knobOffsetOn : toggle.knobOffsetOff,
      transition: toggle.knobTransition,
    }) as SxProps<Theme>,

  toggleLock: {
    fontSize: toggle.lockSize,
    color: toggle.lockColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /* ------------------------------- Company logo ------------------------- */

  /**
   * Both small references stack a full-width dashed frame over the actions;
   * desktop keeps the frame and its notes side by side.
   */
  logoRow: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "stretch", md: "center" },
    gap: logo.rowGap,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  logoBox: {
    // Design measures the dashed frame from inside the border.
    boxSizing: { xs: "border-box", md: "content-box" },
    width: { xs: "100%", md: logo.boxWidth },
    height: logo.boxHeight,
    flexShrink: 0,
    border: logo.boxBorder,
    borderRadius: logo.boxRadius,
    bgcolor: logo.boxBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 0,
    "&:hover": { borderColor: logo.boxHoverBorderColor },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,

  logoImage: {
    maxWidth: logo.imageMaxWidth,
    maxHeight: logo.imageMaxHeight,
    objectFit: "contain",
    display: "block",
  } as const,

  logoInfo: {
    display: "flex",
    // The reference puts Replace / Remove on one line with the size hint
    // trailing it; desktop stacks the hint above the links.
    flexDirection: { xs: "row-reverse", md: "column" },
    alignItems: { xs: "center", md: "stretch" },
    justifyContent: { xs: "space-between", md: "flex-start" },
    gap: logo.infoGap,
    minWidth: 0,
    fontSize: logo.infoFontSize,
    lineHeight: logo.infoLineHeight,
    color: logo.infoColor,
  } as SxProps<Theme>,

  logoLinkRow: {
    display: "flex",
    gap: logo.linkGap,
  } as SxProps<Theme>,

  logoLink: (variant: "replace" | "remove") =>
    ({
      p: 0,
      minWidth: 0,
      // ButtonBase renders a <button>, which would otherwise fall back to the
      // browser's UI font instead of Montserrat.
      fontFamily: "inherit",
      fontSize: logo.infoFontSize,
      fontWeight: logo.linkFontWeight,
      lineHeight: logo.infoLineHeight,
      color: variant === "replace" ? logo.replaceColor : logo.removeColor,
      "&:hover": {
        color:
          variant === "replace" ? logo.replaceHoverColor : logo.removeHoverColor,
        bgcolor: "transparent",
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  logoError: {
    color: logo.errorColor,
    fontSize: logo.infoFontSize,
    lineHeight: logo.infoLineHeight,
  } as SxProps<Theme>,

  /* ---------------------------- Text fields ----------------------------- */

  fieldGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      sm: "minmax(0, 1fr) minmax(0, 1fr)",
    },
    rowGap: field.rowGap,
    columnGap: field.columnGap,
  } as SxProps<Theme>,

  fieldLabel: {
    color: field.labelColor,
    fontSize: field.labelFontSize,
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    ml: 0,
    // Same selector CustomInput's base style uses, so this overrides it rather
    // than racing an equally-specific rule.
    "& .MuiFormLabel-asterisk": { color: field.requiredColor },
  } as SxProps<Theme>,

  textField: {
    "& .MuiOutlinedInput-root": {
      height: { xs: r.field.height, md: field.height },
      p: 0,
      borderRadius: field.radius,
      bgcolor: field.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
        borderWidth: "1px",
      },
      "&:hover": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-focused": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: field.focusBorder,
          borderWidth: "1px",
        },
      },
      "&.Mui-disabled": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "error.main",
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: `0 ${field.paddingX}`,
      fontSize: { xs: r.field.fontSize, md: field.fontSize },
      fontWeight: field.fontWeight,
      // The theme forces 24px on every outlined input; the design leaves it be.
      lineHeight: "normal",
      color: field.color,
      "&::placeholder": {
        color: field.placeholder,
        opacity: 1,
        fontSize: field.fontSize,
        fontWeight: field.fontWeight,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: field.color,
        color: field.color,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "4px",
      fontSize: field.errorFontSize,
    },
  } as SxProps<Theme>,

  /** Footer box: same chrome as the fields but a fixed-height multi-line area. */
  footerField: {
    "& .MuiOutlinedInput-root": {
      height: textarea.height,
      alignItems: "flex-start",
      p: textarea.padding,
      borderRadius: field.radius,
      bgcolor: field.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
        borderWidth: "1px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: field.focusBorder,
        borderWidth: "1px",
      },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      p: 0,
      overflow: "auto",
      resize: "none",
      fontSize: field.fontSize,
      fontWeight: field.fontWeight,
      lineHeight: textarea.lineHeight,
      color: field.color,
      "&::placeholder": {
        color: field.placeholder,
        opacity: 1,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: field.color,
        color: field.color,
      },
    },
  } as SxProps<Theme>,

  /* ---------------------------- Brand colour ---------------------------- */

  /** Stack of labelled colour groups inside the "Branding Colors" card. */
  colorGroups: {
    display: "flex",
    flexDirection: "column",
    gap: brandColor.groupGap,
  } as SxProps<Theme>,

  colorGroupLabel: {
    display: "flex",
    alignItems: "center",
    gap: brandColor.infoGap,
    mb: brandColor.labelGap,
    color: brandColor.labelColor,
    fontSize: brandColor.labelFontSize,
    fontWeight: brandColor.labelFontWeight,
    lineHeight: "normal",
  } as SxProps<Theme>,

  colorGroupInfo: {
    fontSize: brandColor.infoSize,
    color: brandColor.infoColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  swatchRow: {
    display: "flex",
    alignItems: "center",
    gap: brandColor.rowGap,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  swatch: (color: string, selected: boolean) =>
    ({
      // The white ring sits outside the 34px colour field, as in the design.
      boxSizing: "content-box",
      width: brandColor.swatchSize,
      height: brandColor.swatchSize,
      flexShrink: 0,
      borderRadius: brandColor.swatchRadius,
      bgcolor: color,
      border: brandColor.swatchRing,
      outline: selected
        ? `${brandColor.swatchOutlineWidth} solid ${color}`
        : "none",
      "&:hover": {
        outline: `${brandColor.swatchOutlineWidth} solid ${color}`,
      },
    }) as SxProps<Theme>,

  /**
   * Read-out chip; doubles as the trigger for the native colour picker.
   * `offset` adds the design's extra 8px separating it from the preset row.
   */
  colorChip: (disabled: boolean, offset: boolean) =>
    ({
      position: "relative",
      ml: offset ? brandColor.chipGapBefore : 0,
      height: brandColor.chipHeight,
      px: brandColor.chipPaddingX,
      display: "inline-flex",
      alignItems: "center",
      gap: brandColor.chipGap,
      boxSizing: "border-box",
      border: `1px solid ${brandColor.chipBorder}`,
      borderRadius: brandColor.chipRadius,
      bgcolor: brandColor.chipBg,
      cursor: disabled ? "default" : "pointer",
      "&:focus-within": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  colorChipDot: (color: string) =>
    ({
      width: brandColor.chipDotSize,
      height: brandColor.chipDotSize,
      flexShrink: 0,
      borderRadius: brandColor.chipDotRadius,
      bgcolor: color,
    }) as SxProps<Theme>,

  colorChipValue: {
    fontFamily: brandColor.chipFontFamily,
    fontSize: brandColor.chipFontSize,
    fontWeight: brandColor.chipFontWeight,
    color: brandColor.chipColor,
    lineHeight: "normal",
    textTransform: "uppercase",
  } as SxProps<Theme>,

  /**
   * Invisible native picker filling the chip, so the OS colour popup anchors to
   * it while the chip itself keeps the design's chrome.
   */
  colorInput: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    p: 0,
    border: "none",
    opacity: 0,
    pointerEvents: "none",
  } as SxProps<Theme>,

  contrastMessage: (blocking: boolean) =>
    ({
      color: blocking ? brandColor.warningColor : brandColor.cautionColor,
      fontSize: brandColor.warningFontSize,
      lineHeight: brandColor.warningLineHeight,
    }) as SxProps<Theme>,

  /* --------------------------- Preview panel ---------------------------- */

  previewColumn: {
    // Below `md` the reference runs the preview the full content width, under
    // the settings cards rather than beside them.
    flex: { xs: "1 1 100%", md: `1 1 ${layout.previewBasis}` },
    maxWidth: { xs: "100%", md: layout.previewMaxWidth },
    minWidth: 0,
    boxSizing: "border-box",
    p: preview.padding,
    border: `1px solid ${preview.border}`,
    borderRadius: preview.radius,
    bgcolor: preview.bg,
    display: "flex",
    flexDirection: "column",
    gap: preview.gap,
    position: { md: "sticky" },
    top: { md: `${layout.previewStickyTop}px` },
  } as SxProps<Theme>,

  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: preview.headerGap,
  } as SxProps<Theme>,

  previewTitle: {
    fontSize: preview.titleFontSize,
    fontWeight: preview.titleFontWeight,
    letterSpacing: preview.titleLetterSpacing,
    color: preview.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  previewBadge: {
    ml: "auto",
    flexShrink: 0,
    p: preview.badgePadding,
    borderRadius: preview.badgeRadius,
    bgcolor: preview.badgeBg,
    color: preview.badgeColor,
    fontSize: preview.badgeFontSize,
    fontWeight: preview.badgeFontWeight,
    letterSpacing: preview.badgeLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  previewCaption: {
    fontSize: preview.captionFontSize,
    color: preview.captionColor,
    lineHeight: preview.captionLineHeight,
    textAlign: "center",
  } as SxProps<Theme>,

  emailCard: {
    bgcolor: "background.paper",
    borderRadius: email.radius,
    boxShadow: email.shadow,
    overflow: "hidden",
  } as SxProps<Theme>,

  emailHeader: {
    display: "flex",
    alignItems: "center",
    gap: email.headerGap,
    p: email.headerPadding,
    borderBottom: `1px solid ${email.headerBorder}`,
  } as SxProps<Theme>,

  emailAvatar: (color: string) =>
    ({
      width: email.avatarSize,
      height: email.avatarSize,
      flexShrink: 0,
      bgcolor: color,
      color: email.avatarColor,
      fontSize: email.avatarFontSize,
      fontWeight: email.avatarFontWeight,
    }) as SxProps<Theme>,

  emailSenderText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  } as SxProps<Theme>,

  emailSender: {
    fontSize: email.senderFontSize,
    color: email.senderColor,
    lineHeight: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  emailSenderMuted: {
    color: email.senderMutedColor,
    fontWeight: 400,
  } as SxProps<Theme>,

  emailRecipient: {
    fontSize: email.recipientFontSize,
    color: email.senderMutedColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  emailBody: {
    p: email.bodyPadding,
    display: "flex",
    flexDirection: "column",
    gap: email.bodyGap,
    fontSize: email.bodyFontSize,
    lineHeight: email.bodyLineHeight,
    color: email.bodyColor,
  } as SxProps<Theme>,

  emailLogo: {
    width: email.logoWidth,
    objectFit: "contain",
    display: "block",
    flexShrink: 0,
  } as const,

  emailStrong: {
    color: email.strongColor,
    fontWeight: 700,
  } as SxProps<Theme>,

  emailCta: (bg: string, label: string) =>
    ({
      alignSelf: "flex-start",
      p: email.ctaPadding,
      my: email.ctaMarginY,
      borderRadius: email.ctaRadius,
      bgcolor: bg,
      color: label,
      fontSize: email.ctaFontSize,
      fontWeight: email.ctaFontWeight,
    }) as SxProps<Theme>,

  emailFooterText: {
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  } as SxProps<Theme>,

  emailLink: {
    color: email.linkColor,
    textDecoration: "underline",
    wordBreak: "break-all",
  } as SxProps<Theme>,

  emailFooterRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    borderTop: `1px solid ${email.headerBorder}`,
    pt: email.legalPaddingTop,
  } as SxProps<Theme>,

  emailLegal: {
    fontSize: email.legalFontSize,
    color: email.legalColor,
    textAlign: "right",
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  } as SxProps<Theme>,

  /* ------------------------------- Actions ------------------------------ */

  primaryButton: {
    // Both small references run it full width, closing the screen.
    width: { xs: "100%", md: "auto" },
    height: { xs: r.primaryButton.height, md: primaryButton.height },
    px: primaryButton.paddingX,
    borderRadius: primaryButton.radius,
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    fontSize: primaryButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
    "&.Mui-disabled": {
      bgcolor: primaryButton.disabledBg,
      color: primaryButton.disabledColor,
      cursor: "not-allowed",
      pointerEvents: "auto",
    },
    "& .MuiButton-endIcon": {
      ml: "8px",
      "& > *:first-of-type": { fontSize: primaryButton.lockSize },
    },
  } as SxProps<Theme>,
};

export default brandingStyles;
